<script setup>
  import { computed, ref, toRaw } from 'vue';
  import deepmerge from 'deepmerge';

  import { BANK_CODES } from '@js/constants/banks';

  import CredereAlert from '@/components/Credere/Alert.vue';
  import CredereButtons from '@/components/Credere/Buttons.vue';
  import CredereButton from '@/components/Credere/Button.vue';
  import CredereHeader from '@/components/Credere/Header.vue';

  import { LAST_INLINE_KEY } from './javascripts/inline';

  import useButtoner from './composables/buttoner';
  import useClasser from './composables/classer';
  import usePlanter from './composables/planter';
  import useSubmitter, { BYPASS } from './composables/submitter';

  import filled from './javascripts/filled';
  import fieldName, { cloneName } from './javascripts/name';

  import FormField from './Field.vue';

  const props = defineProps({
    banks: { type: Array, default: () => BANK_CODES, required: false },
    banksInclusion: { type: Array, default: () => BANK_CODES, required: false },

    initializer: { type: Function, required: true },
    entity: { type: Object, default: () => ({}), required: false },

    settings: { type: Object, default: () => ({}), required: false },

    callbacks: { type: Object, default: () => ({}), required: false },
    combine: { type: Object, default: () => ({}), required: false },
    constants: { type: Object, default: () => ({}), required: false },
    sizes: { type: Object, default: () => ({}), required: false },
    ungroup: { type: Object, default: () => ({}), required: false },
    sorts: { type: Object, default: () => ({}), required: false },
    tips: { type: Object, default: () => ({}), required: false },
    clones: { type: Object, default: () => ({}), required: false },
    filters: { type: Object, default: () => ({}), required: false },
    inlines: { type: Object, default: () => ({}), required: false },

    domains: { type: Object, default: () => ({}), required: false },

    endpoint: { type: [Array, String], required: true },
    singular: { type: [Array, String], required: true },
    plural: { type: [Array, String], required: false },

    data: { type: [Array, String], required: false },
    action: { type: String, required: false },

    identifier: { type: [Array, String], default: (x) => (Array.isArray(x.singular) ? x.singular.map(() => 'id') : 'id'), required: false },
    chain: { type: [Array, Function], default: () => (() => null), required: false },

    normalizer: { type: Function, default: (() => null), required: false },
    parser: { type: Function, default: (() => null), required: false },

    disabled: { type: Boolean, default: false, required: false },

    variants: { type: Object, default: () => ({}), required: false },

    qrcodable: { type: Boolean, default: false, required: false },

    primaryButton: { type: [String, Object], default: () => ({ text: 'Salvar', icon: null }), required: false },
    secondaryButton: { type: [String, Object], required: false },
  });

  const emit = defineEmits([
    'force',

    'success',
    'failure',
    'chain',

    'blur',

    'inline-toggle',
  ]);

  const refForm = ref();

  const variants = computed(() => deepmerge(window.CREDERE.variants, props.variants, {}));

  const alert = ref(false);
  const extra = ref('');

  const planter = usePlanter(props);
  const { buttons, toggle: toggleButtons } = useButtoner(props);

  const {
    classes,

    inlineEnabled,
    inlineVisible,
    inlineToggle,
  } = useClasser(props, planter);

  const { submit } = useSubmitter(
    props,
    planter,

    alert,
    extra,

    emitSuccess,
    emitFailure,
    emitChain,

    toggleButtons,
  );

  // eslint-disable-next-line vue/no-dupe-keys
  const {
    entity,

    leaves,
    values,
    properties,

    errors,
    loadings,
    readonlies,
    visibilities,

    tree,
  } = planter;

  let timeout;
  function force() {
    emit('force');

    clearTimeout(timeout);

    timeout = setTimeout(() => {
      submit({
        target: {
          form: refForm.value,
        },
      }, buttons.value[0].hierarchy);
    }, 300);
  }

  function change(name, value) {
    values.value[name] = value;
  }

  function changing(name, value) {
    if (typeof name === 'object') {
      Object.keys(name).forEach((i) => changing(i, name[i]));
    } else {
      change(name, value);
    }

    if (inlineEnabled.value) {
      force();
    }
  }

  function blurring(name) {
    values.value[name] = { blur: true };

    emit('blur', name);
  }

  function destroying(name) {
    if (properties.value[name] === 'file') {
      changing(name, '');
      blurring(name);
    }

    if (leaves.value[properties.value[name].destroyer]) {
      values.value[properties.value[name].destroyer] = !destroyed(name);
    } else {
      properties.value[name].combining.forEach((i) => {
        values.value[i] = '';
      });
    }

    try {
      document.querySelector(`input[type="file"][name="${name}"]`).value = '';
    } catch (err) { /* */ }
  }

  function destroyed(name) {
    return properties.value[name].destroyed || [true, 'true'].includes(values.value[properties.value[name].destroyer]);
  }

  function clonable(branch) {
    return planter._COMPOSABLES.clonable(branch.name);
  }

  function clone(branch) {
    planter._COMPOSABLES.clone(branch.name);
    planter._REPLANT();
  }

  function _inlineToggle() {
    inlineToggle();

    if (inlineVisible.value) {
      return;
    }

    const key = props.data || (props.action === 'list' ? props.plural : props.singular);

    Object
      .keys(props.settings._inline?.defaults || {})
      .forEach((identifier) => {
        const _name = fieldName(key, identifier);

        if (leaves.value[_name]) {
          change(_name, props.settings._inline.defaults[identifier]);
        }
      });

    force();
  }

  function emitSuccess(hierarchy, action, index, parsed, response) {
    emit('success', hierarchy, action, index, parsed, response);
  }

  function emitFailure(hierarchy, action, index, parsed, error) {
    emit('failure', hierarchy, action, index, parsed, error);
  }

  function emitChain(hierarchy, action, index, parsed, response) {
    const _entity = props
      .singular
      .reduce((reduced, singular) => {
        reduced[singular] = singular === props.singular[index]
          ? props.chain[index](response) : toRaw(entity.value[singular]);

        return reduced;
      }, {});

    entity.value = _entity;

    emit('chain', hierarchy, action, index, parsed, response);
  }

  const tag = computed(() => (props.endpoint === BYPASS ? 'section' : 'form'));

  const styles = computed(() => ({
    'credere-form': true,
    [`variant--section-1${variants.value.section?.toLowerCase()}`]: variants.value.section,
  }));

  defineExpose({
    force,
    planter,
  });
</script>

<template>
  <component :is="tag"
    ref="refForm"
    :class="styles"
  >
    <slot />

    <credere-alert v-if="alert">
      <template
        v-if="extra"
        #extra
      >
        {{ extra }}
      </template>
    </credere-alert>

    <ul class="credere-form-tree">
      <li
        v-for="branch in tree"
        :key="branch.name"
        :class="classes.branches[branch.name]"
        :data-branch="branch.name"
      >
        <slot :name="branch.leaves[0]" />

        <credere-header
          v-if="!inlineEnabled"

          class="credere-form-header"

          :title="branch.title"
          :subtitle="branch.subtitle"
        />

        <slot :name="`header:${branch.name}`" />

        <ul class="credere-form-leaves">
          <li
            v-for="(name, index) in branch.leaves"
            :key="name"
            :class="classes.leaves[name]"
            :data-leaf="name"
            :data-empty="!filled(values[name])"
            :style="{ order: leaves[name].order || index }"
          >
            <form-field
              :values="values"
              :domains="domains"

              :inline="!!inlines[name]"
              :qrcodable="qrcodable"

              :name="name"
              :leaf="leaves[name]"
              :properties="properties[name]"
              :error="errors[name]"
              :loading="loadings[name]"
              :visibility="visibilities[name]"
              :readonly="readonlies[name]"
              :tip="tips[name] || tips[cloneName(name)]"

              :variant="variants.field"

              :value="values[name]"
              @update:value="value => changing(name, value)"
              @update:values="values => changing(values)"

              @blur="blurring(name)"

              :destroyed="destroyed(name)"
              @destroy="destroying(name)"
            />

            <template v-if="inlines[name] === LAST_INLINE_KEY">
              <credere-button
                :icon="inlineVisible ? 'close' : 'sliders'"
                :color="inlineVisible ? 'danger' : undefined"
                @click.prevent="_inlineToggle"
              />
            </template>
          </li>
        </ul>

        <credere-buttons
          v-if="clonable(branch)"
          class="credere-form-leaves-clone"
          align="left"
          spacing="none"
          borderless
        >
          <credere-button
            icon="plus"
            color="highlight"
            :variant="buttons[0].variant"
            :variant-secondary="buttons[0].variantSecondary"
            @click.stop.prevent="clone(branch)"
          >
            {{ clonable(branch) }}
          </credere-button>
        </credere-buttons>

        <slot :name="`footer:${branch.name}`" />
      </li>
    </ul>

    <credere-buttons
      v-if="!inlineEnabled"
      class="credere-form-buttons"
      :variant="variants.section"
    >
      <slot name="buttons" />

      <credere-button
        v-for="button in buttons"
        :key="button.text"
        :icon="button.icon"
        :color="button.color"
        :loading="button.loading"
        :disabled="button.disabled || disabled"
        :variant="button.variant"
        :variant-secondary="button.variantSecondary"

        @click="e => submit(e, button.hierarchy)"
      >
        {{ button.text }}
      </credere-button>
    </credere-buttons>
  </component>
</template>

<style lang="scss">
  .credere-form {
  }

  .credere-form-tree,
  .credere-form-leaves {
    list-style: none;
  }

  .credere-form-branch {
    --border-color: var(--token-color-constant-dark);
    --border-style: solid;
    --border-width: var(--token-border-width-thin) 0 0 0;
    --margin: var(--token-spacing-blaster) 0 0 0;
    --padding: var(--token-spacing-blaster) 0 0 0;

    &.variant--top {
      --border-width: 0;
      --padding: 0;
      --margin: 0;
    }

    & + &.variant--clone {
      @container (min-width: 769px) {
        --border-width: 0;
        --padding: 0;
        --margin: var(--token-spacing-giga) 0 0 0;
      }
    }

    .variant--section-1b & {
      --border-radius: var(--token-border-radius-decorative);
      --border-color: var(--token-color-neutral-light);
      --border-style: solid;
      --border-width: var(--token-border-width-soft);
      --background-color: var(--token-color-constant-light);
      --box-shadow: var(--token-box-shadow-around-2);
      --margin: var(--token-spacing-xl) 0 0 0;
      --padding: var(--token-spacing-xl);

      &.variant--top {
        --margin: 0;
      }
    }

    &.state--hidden {
      background-color: #FFC;
      display: none;
    }
  }

  .credere-form-leaves {
    --gap: var(--token-spacing-giga) var(--token-spacing-s);

    display: grid;
    grid-template-columns: repeat(4, 1fr);
    list-style: none;

    .variant--clonable & {
      grid-template-columns: repeat(4, 1fr) auto;
    }

    @container (max-width: 768px) {
      --gap: var(--token-spacing-s);

      align-items: flex-start;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      flex-wrap: wrap;
    }
  }

  .credere-form-leaf {
    --gap: var(--token-spacing-s);

    align-items: flex-start;
    display: flex;
    flex-direction: row;
    grid-column: span 1;
    justify-content: center;
    min-width: 0;

    &.size--2 {
      grid-column: span 2;
    }

    &.size--3 {
      grid-column: span 3;
    }

    &.size--4 {
      grid-column: span 4;
    }

    @container (max-width: 768px) {
      min-width: 34%;
      flex: 1;

      &.size--2,
      &.size--3,
      &.size--4 {
        flex-basis: 100%;
      }

      &.variant--destroyer {
        flex-basis: 100%;
      }
    }

    @container (max-width: 280px) {
      flex-basis: 100%;
    }

    &.state--hidden {
      background-color: #FCC;
      display: none;
      opacity: 0.7;
      transform: scale(0.8) rotate(-3deg);
    }
  }

  .credere-form-leaves-clone {
    --margin: var(--token-spacing-xl) 0 0;

    @container (max-width: 768px) {
      --margin: var(--token-spacing-giga) 0 0;
    }
  }

  .credere-form-header {
    .variant--clone & {
      display: none;
    }
  }
</style>
